import type { User } from '@/types';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user';

/**
 * Auth utility functions for managing JWT tokens and user data
 */
export const auth = {
  /**
   * Get the current JWT token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Store JWT token in localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  },

  /**
   * Remove JWT token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  },

  /**
   * Get the current user from localStorage
   */
  getUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      const user = JSON.parse(userStr) as User;
      // Ensure permissions is always an array for backwards compatibility
      if (!Array.isArray(user.permissions)) {
        user.permissions = [];
      }
      return user;
    } catch {
      return null;
    }
  },

  /**
   * Store user data in localStorage
   */
  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  /**
   * Remove user data from localStorage
   */
  removeUser(): void {
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Clear all auth data
   */
  clear(): void {
    this.removeToken();
    this.removeUser();
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  },
};
